import { Box, Button, Grid, Typography } from '@mui/material'
import section2 from '../../Assets/Images/Home-image.webp'
import video from '../../Assets/Videos/video.mp4'
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ShieldCheck, FileCheck, Building2, PhoneCallIcon } from "lucide-react";
import "aos/dist/aos.css";
import AOS from "aos";
import OurMission from '../../Components/OurMission/OurMission';
import Leadership from '../../Components/Leadership/Leadership';
import OperatingStructure from '../../Components/OperatingStructure/OperatingStructure';

function Home() {
  const videoRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: false,
      offset: 100,
      easing: 'ease-out-cubic',
    });
  }, []);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const START_TIME = 5;
    let END_TIME = 0;

    // Start video from 5 seconds
    const handleLoaded = () => {
      END_TIME = videoElement.duration - 5; // 🔥 cut last 5 sec
      videoElement.currentTime = START_TIME;
      videoElement.playbackRate = 1;
    };

    // Prevent playing last 5 sec
    const handleTimeUpdate = () => {
      if (videoElement.currentTime >= END_TIME) {
        videoElement.currentTime = START_TIME;
      }
    };

    videoElement.addEventListener("loadedmetadata", handleLoaded);
    videoElement.addEventListener("timeupdate", handleTimeUpdate);

    // Intersection Observer (play only in viewport)
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (videoElement.paused) {
            videoElement.play().catch(() => { }); // ✅ prevent AbortError
          }
        } else {
          if (!videoElement.paused) {
            videoElement.pause();
          }
        }
      },
      { threshold: 0.4 } // ✅ lower threshold (0.6 causes flicker)
    );

    observer.observe(videoElement);

    return () => {
      videoElement.removeEventListener("loadedmetadata", handleLoaded);
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);
      observer.disconnect();
    };
  }, []);

  const points = [
    {
      text: "Maintain strict timelines",
      icon: Clock
    },
    {
      text: "Control build quality at every stage",
      icon: ShieldCheck
    },
    {
      text: "Ensure regulatory compliance",
      icon: FileCheck
    },
    {
      text: "Deliver structured, stress-free building experiences",
      icon: Building2
    }
  ];

  return (
    <Box className='h-100 w-100'>
      <Box position="relative" width="100%" overflow="hidden" sx={{ minHeight: { xs: '260px', md: '500px' } }}>

        {/* Video */}
        <video
          ref={videoRef}
          src={video}
          autoPlay
          loop
          muted
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />

        {/* Dark Overlay */}
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          sx={{
            // backgroundColor: "rgba(0, 0, 0, 0.4)",
            background: "linear-gradient(to right, rgba(0,0,0,0.1), rgba(0,0,0,0.7))",
          }}
        />

        {/* Responsive Text */}
        <Box
          position="absolute"
          sx={{
            top: { xs: "50%", md: "30%" },
            left: { xs: "50%", md: "auto" },
            right: { xs: "auto", md: "5%" },
            transform: {
              xs: "translate(-50%, -50%)",
              md: "none",
            },
            textAlign: { xs: "center", md: "right" },
            color: "#fff",
            width: { xs: "90%", md: "auto" },
          }}
        >
          <Typography
            fontSize={{ xs: "24px", md: "72px" }}
            sx={{
              letterSpacing: 3,
              fontWeight: 700,
              lineHeight: 1.2,
              textShadow: "0 6px 30px rgba(0,0,0,0.6)",
            }}
          >
            RAISING BENCHMARKS <br />
            <span className="text-main">EVERYTIME</span>
          </Typography>
          <Typography
            display={{ xs: 'none', md: 'inline-block' }}
            fontSize={{ xs: "14px", md: "20px" }}
          >Delivering bespoke homes crafted with precision and timeless elegance.</Typography>

          <Box mt={{ xs: 3, md: 5 }} className='d-flex' justifyContent={{ xs: "center", md: "end" }} flexWrap={'wrap'} gap={2}>
            <Button variant='outlined' fontSize={{ xs: "12px", md: "16px" }} onClick={() => navigate('/contact')} className='fs-14  btn-main  border-20'>Let’s Discuss Your Dream Home</Button>
            <Button component="a" href="tel:1300451780" variant='outlined' fontSize={{ xs: "12px", md: "16px" }} className='fs-14  btn-outlined-second  border-20'><PhoneCallIcon /> &nbsp; 1300 451 780</Button>
          </Box>
        </Box>

      </Box>

      <Box
        position="relative"
        mt={{ xs: 5, md: 12 }}
        sx={{
          backgroundImage: { xs: "", md: `url(${section2})` },
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >

        {/* Soft White Overlay */}
        <Box
          sx={{
            background: "linear-gradient(to right, rgba(255,255,255,0.95) 60%, rgba(255,255,255,0) 100%)",
            px: { xs: 2, md: 8 },
            py: { xs: 5, md: 8 },
          }}
        >

          {/* Heading */}
          <Typography data-aos="fade-up" className="fw-bold text-second fs-42" gutterBottom>
            WHO WE ARE
          </Typography>

          {/* Description */}
          <Typography data-aos="fade-up" className="fs-16 text-dark" mb={4} maxWidth="700px">
            Clarence Homes is a Sydney-based residential construction company specialising in single and double-storey homes built to exceptional standards.
            Established in 2019 by an experienced Project Manager, Clarence Homes was founded with a clear and unwavering vision — to raise the standard of residential construction across Sydney.
            As a licensed NSW builder, we combine structured project management expertise with practical construction knowledge to deliver homes defined by precision, transparency, and structural integrity.
            Every project is approached with the mindset that we are building for our own family. Through strict internal systems, disciplined quality control, and uncompromising attention to detail, we ensure every home reflects craftsmanship we are proud to stand behind.
            We are not simply constructing houses — we are setting new benchmarks in residential building.
          </Typography>

          {/* CTA Button */}
          <Box display="flex" justifyContent="center" mt={5}>
            <Button
              onClick={() => navigate('/about')}
              className='btn-outlined-main border-10'
            >
              LEARN MORE ABOUT US
            </Button>
          </Box>

        </Box>
      </Box>


      <Box
        position="relative"
        mt={{ xs: 5, md: 12 }}

        sx={{
          backgroundImage: { xs: "none", md: `url(${section2})` },
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >

        {/* Soft White Overlay */}
        <Box
          sx={{
            background: "linear-gradient(to left, rgba(255,255,255,0.95) 60%, rgba(255,255,255,0.6) 100%)",
            px: { xs: 2, md: 8 },
            py: { xs: 5, md: 8 },
          }}
        >

          {/* Heading */}
          <Typography className="fw-bold text-end text-second fs-42" gutterBottom>
            OUR EXPERIENCE
            <Box
              data-aos="fade-up"
              sx={{
                width: { xs: '60px', md: '80px' },
                height: '4px',
                backgroundColor: 'var(--primary-text-color)',
                marginLeft: 'auto',
                mb: 4,
                mt: 3
              }}
            />
          </Typography>

          {/* Description */}
          <Typography className="fs-16 text-dark ms-auto" mb={4} maxWidth="700px">
            Since 2019, our leadership has been actively involved in managing residential construction projects across Sydney.
            With a background in project management, we understand that successful home building is not just about construction —
            it’s about planning, communication, budgeting, compliance, and execution.
          </Typography>

          {/* Feature Cards */}
          <Grid container spacing={3} className='w-100'>

            {points.map((item, index) => {
              const IconComponent = item.icon;

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={index}
                  sx={{
                    maxWidth: { md: '300px' },
                    minWidth: { md: '250px' }
                  }}
                >
                  <Box
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                    sx={{
                      backgroundColor: '#ffffff',
                      p: 3,
                      borderRadius: 2,
                      height: '100%',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease',
                      border: '1px solid transparent',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                        borderColor: 'var(--primary-text-color)',
                        '& .mission-icon': {
                          backgroundColor: 'var(--primary-color)'
                        },
                        '& .mission-icon svg': {
                          color: 'var(--primary-text-color)'
                        }
                      }
                    }}
                  >
                    {/* Icon Circle */}
                    <Box
                      className="mission-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: 'var(--secondary-color)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        transition: 'all 0.3s ease',
                        '& svg': {
                          color: 'var(--primary-color)',
                          transition: 'color 0.3s ease'
                        }
                      }}
                    >
                      <IconComponent size={28} />
                    </Box>

                    <Typography
                      className="fw-bold text-second"
                      sx={{ fontSize: '18px', mb: 1 }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {/* CTA Button */}
          <Box display="flex" justifyContent="flex-start" mt={5}>
            <Button onClick={() => navigate('/about')} className='btn-outlined-main border-10'>
              LEARN MORE ABOUT US
            </Button>
          </Box>

        </Box>
      </Box>

      {/* Our Mission Section */}
      <OurMission />

      {/* Leadership & Management Section */}
      <Leadership />

      {/* Operating Structure Section */}
      <OperatingStructure />

    </Box>
  )
}

export default Home

